/**
 * Spaces department items — third band of the home timeline (zone
 * 'spaces', progress band 0.5–0.75). Visual layout: the central display box
 * (tagged maskRegion 'spaces' — the spaces WebGL visual clips inside it), a
 * floor-highlight wash and bead-curtain backdrop, two illustrated characters
 * ('Amy' right, 'Bene' left — both pass 2), three very large gradient blobs,
 * and a 21-particle swarm of gradient ambience dots flanking the content box
 * on both sides (relativeX < 0 = screens side, > 1 = systems side).
 *
 * Classification rule for this file: `zone: 'spaces'` tag (every item here is
 * tagged, including the three 'largescreenp' ids).
 *
 * Grouping is for HUMANS ONLY — creation order is owned by index.ts (see the
 * order contract there). Field semantics: see HomeItemConfig / the underlying
 * TimelineItemConfig.
 */
import type { HomeItemConfig } from './homeItems.types';

/**
 * Very large, heavily faded gradient blob over the middle of the spaces zone
 * (ref 'faded right bottom very big'). Ambience wash behind the department content —
 * the 'largescreenp' id notwithstanding, it is zoned to spaces.
 */
export const largeScreenP: HomeItemConfig = {
	disable: false,
	id: 'largescreenp',
	ref: 'faded right bottom very big',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 0.5,
	relativeY: 0.29,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	minScale: 40,
	maxScale: 35,
	autoRange: 0.3,
	repell: false,
	alpha: 0.13
};

/**
 * Sibling of largeScreenP: very large faded gradient blob pinned to the left edge
 * of the spaces zone (ref 'faded left bottom very big').
 */
export const largeScreenP2: HomeItemConfig = {
	disable: false,
	id: 'largescreenp2',
	ref: 'faded left bottom very big',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 0,
	relativeY: 0.395,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	minScale: 35,
	maxScale: 35,
	autoRange: 0.3,
	repell: false,
	alpha: 0.13
};

/**
 * Sibling of largeScreenP: very large faded gradient blob at the right of the
 * spaces zone (ref 'faded right bottom very big').
 */
export const largeScreenP3: HomeItemConfig = {
	disable: false,
	id: 'largescreenp3',
	ref: 'faded right bottom very big',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 0.9,
	relativeY: 0.3955,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.03,
	autoScale: false,
	startScale: 0.0001,
	minScale: 35,
	maxScale: 35,
	autoRange: 0.3,
	repell: false,
	alpha: 0.13
};

/**
 * Wide floor-highlight wash (home-right-floor-large.png) sitting low in the
 * spaces zone (relativeY 0.72) — a soft pool of light under the department content.
 */
export const floorHighlight: HomeItemConfig = {
	id: 'floorhighligt',
	texture: 'home-right-floor-large.png',
	relativeX: 0.5,
	relativeY: 0.72,
	easePower: 5,
	parallaxOffsetPower: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	alpha: 0.5,
	minScale: 1.5,
	maxScale: 1.5,
	zone: 'spaces',
	ySizeShift: 0
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Repells away from the cursor.
 */
export const spacesP1: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.2,
	relativeY: 0.405,
	relativeXMobile: 1.05,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.6,
	autoRange: 0.9,
	repell: true,
	alpha: 0.3
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Repells away from the cursor.
 */
export const spacesP2: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.396,
	relativeXMobile: 1.1,
	relativeY: 0.33,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.028,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.5,
	autoRange: 0.7,
	repell: true,
	alpha: 0.2
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Repells away from the cursor.
 */
export const spacesP3: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.694,
	relativeXMobile: 0.95,
	relativeY: 0.53,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.4,
	autoRange: 0.7,
	repell: true,
	alpha: 0.1
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone).
 */
export const spacesP4: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.303,
	relativeXMobile: 0.7,
	relativeY: 0.38,
	relativeYMobile: 0.15,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	autoRange: 0.5,
	repell: false,
	alpha: 0.35,
	minScale: 0.65,
	maxScale: 0.65
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone).
 */
export const spacesP5: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.525,
	relativeXMobile: 1.1,
	relativeY: 0.58,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	autoRange: 0.3,
	repell: false,
	alpha: 0.1
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Designer ref: 'Tiny one'.
 */
export const spacesP6: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'Tiny one',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.2,
	relativeXMobile: 0.9,
	relativeY: 0.3,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.01,
	parallaxPowerMouse: 0.01,
	autoScale: false,
	startScale: 0.0001,
	minScale: 0.2,
	maxScale: 0.2,
	autoRange: 0.3,
	repell: false,
	alpha: 0.6
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Designer ref: 'faded right top'.
 */
export const spacesP7: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded right top',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.68,
	relativeXMobile: 1.12,
	relativeY: 0.4,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.05,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 4,
	maxScale: 5,
	autoRange: 0.3,
	repell: false,
	alpha: 0.055
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Designer ref: 'Tiny one 2'.
 */
export const spacesP8: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'Tiny one 2',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.135,
	relativeY: 0.5,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.01,
	parallaxPowerMouse: 0.01,
	autoScale: false,
	startScale: 0.0001,
	minScale: 0.2,
	maxScale: 0.2,
	autoRange: 0.3,
	repell: false,
	alpha: 0.6
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Designer ref: 'faded right bottom'.
 */
export const spacesP9: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded right bottom',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.4,
	relativeXMobile: 1.1,
	relativeY: 0.65,
	relativeYMobile: 0.75,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 4,
	maxScale: 4,
	autoRange: 0.3,
	repell: false,
	alpha: 0.05
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Repells away from the cursor.
 */
export const spacesP10: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.258,
	relativeXMobile: -0.05,
	relativeY: 0.425,
	easePower: 3,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.04,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.7,
	autoRange: 0.95,
	repell: true,
	alpha: 0.18
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Repells away from the cursor.
 * `relativeMobile: 0` is an inert leftover (no consumer reads that key).
 */
export const spacesP11: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.145,
	relativeMobile: 0,
	relativeY: 0.405,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.6,
	autoRange: 0.9,
	repell: true,
	alpha: 0.45,
	minScale: 0.3,
	maxScale: 0.3
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Repells away from the cursor.
 */
export const spacesP12: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.246,
	relativeXMobile: -0.08,
	relativeY: 0.33,
	relativeYMobile: 0.45,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.028,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.5,
	autoRange: 0.7,
	repell: true,
	alpha: 0.28,
	minScale: 0.75,
	maxScale: 0.75
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'Mid left overlap'. Repells away from the cursor.
 */
export const spacesP13: HomeItemConfig = {
	disable: false,
	ref: 'Mid left overlap',
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.494,
	relativeXMobile: -0.08,
	relativeY: 0.53,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.05,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	minScale: 2.2,
	maxScale: 2.2,
	startRangeAt: 0.4,
	autoRange: 0.7,
	repell: true,
	alpha: 0.2,
	hideAtWidth: 1300
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap).
 */
export const spacesP14: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.143,
	relativeXMobile: -0.05,
	relativeY: 0.38,
	relativeYMobile: 0.2,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.01,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	minScale: 0.3,
	maxScale: 0.3,
	autoRange: 0.5,
	repell: false,
	alpha: 0.4
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'small close to screen left'.
 */
export const spacesP15: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'small close to screen left',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.245,
	relativeXMobile: -0.04,
	relativeY: 0.58,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	minScale: 0.5,
	maxScale: 0.5,
	autoRange: 0.3,
	repell: false,
	alpha: 0.65
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'small close on screens overlap'.
 */
export const spacesP16: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'small close on screens overlap',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.565,
	relativeXMobile: -0.04,
	relativeY: 0.33,
	relativeYMobile: 0.1,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.02,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	minScale: 0.4,
	maxScale: 0.3,
	autoRange: 0.3,
	repell: false,
	alpha: 0.4
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'faded big left top'.
 */
export const spacesP17: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded big left top',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.9,
	relativeY: 0.2,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.05,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 4,
	maxScale: 4,
	autoRange: 0.3,
	repell: false,
	alpha: 0.07,
	hideAtWidth: 1300
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'faded mid left top'.
 */
export const spacesP18: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded mid left top',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.8,
	relativeY: 0.4,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.04,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 2,
	maxScale: 2,
	autoRange: 0.3,
	repell: false,
	alpha: 0.18,
	hideAtWidth: 1300
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'small left top'.
 */
export const spacesP19: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'small left top',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.75,
	relativeY: 0.5,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 1,
	maxScale: 1,
	autoRange: 0.3,
	repell: false,
	alpha: 0.22,
	hideAtWidth: 1300
};

/**
 * Gradient ambience particle on the right flank of the spaces content box (toward the systems zone). Designer ref: 'faded right bottom very big'.
 */
export const spacesP20: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded right bottom very big',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: 1.6,
	relativeY: 1,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	startScale: 0.0001,
	minScale: 20,
	maxScale: 20,
	autoRange: 0.3,
	repell: false,
	alpha: 0.04
};

/**
 * Gradient ambience particle on the left flank of the spaces content box (over the screens overlap). Designer ref: 'faded left bottom very big'.
 */
export const spacesP21: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'faded left bottom very big',
	texture: 'gradient-particle.png',
	zone: 'spaces',
	relativeX: -0.8,
	relativeY: 0.8,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.2,
	autoScale: false,
	startScale: 0.0001,
	minScale: 16,
	maxScale: 16,
	autoRange: 0.3,
	repell: false,
	alpha: 0.04,
	hideAtWidth: 1300
};

/**
 * The spaces 'box' (screens-screenarea.png) — the display box at the centre of
 * the spaces zone. maskRegion 'spaces': the spaces WebGL department visual is
 * clipped inside this sprite's bounds.
 */
export const spacesBox: HomeItemConfig = {
	disable: false,
	id: 'box',
	texture: 'screens-screenarea.png',
	relativeX: 0.5,
	relativeY: 0.36,
	relativeYMobile: 0.38,
	startAlpha: 1,
	easePower: 3,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	alpha: 1,
	autoRange: 0.9,
	zone: 'spaces',
	cropXShift: 0,
	mobileScaleIncrease: 0.9,
	maskRegion: 'spaces'
};

/**
 * "Amy" character illustration, base layer (amy-base.png) — right side of the
 * spaces zone. Pass 2 — drawn above the WebGL layer.
 */
export const amyBase: HomeItemConfig = {
	disable: false,
	id: 'amy-base',
	pass: 2,
	texture: 'amy-base.png',
	relativeX: 0.9,
	relativeY: 0.6,
	easePower: 4,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.05,
	zone: 'spaces',
	cropXShift: 0
};

/**
 * "Amy" character illustration, highlight/lighting layer drawn over amyBase.
 * Pass 2.
 */
export const amyHighlight: HomeItemConfig = {
	disable: false,
	id: 'amy-highlight',
	pass: 2,
	texture: 'amy-highlight.png',
	relativeX: 0.9,
	relativeY: 0.6,
	easePower: 4,
	parallaxOffsetPower: 0.03,
	parallaxPowerMouse: 0.05,
	zone: 'spaces',
	cropXShift: 0
};

/**
 * "Bene" character illustration, base layer (bene-base.png) — left side of the
 * spaces zone. Pass 2. (The id reads "personbase".)
 */
export const personBase: HomeItemConfig = {
	disable: false,
	id: 'personbase',
	pass: 2,
	texture: 'bene-base.png',
	relativeX: 0.1,
	relativeY: 0.6,
	easePower: 3,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.1,
	parallaxPowerMouse: 0.04,
	zone: 'spaces'
};

/**
 * "Bene" character illustration, highlight layer (bene-highlight.png). Pass 2.
 * (The id reads "person".)
 */
export const personHighlight: HomeItemConfig = {
	disable: false,
	id: 'person',
	pass: 2,
	texture: 'bene-highlight.png',
	relativeX: 0.1,
	relativeY: 0.6,
	easePower: 3,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.1,
	parallaxPowerMouse: 0.04,
	zone: 'spaces',
	alpha: 1
};
